/**
 * Masked calling in the support console — `/v1/admin/comms*` and the per-booking view.
 *
 * **There is no hook that returns a recording key.** `useRecordingLink` mints a short-lived URL
 * through an endpoint that writes an audit row before it signs anything, so every listen has a
 * name against it. Both parties were told calls may be recorded; that is consent to a support
 * tool, not to an archive somebody can browse.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { authedClient } from '../../auth/session';
import { keys } from '../queryClient';
import type { components } from '../../../api/generated/schema';

export type CommsDetail = components['schemas']['CommsDetail'];
export type MaskedSessionRow = components['schemas']['MaskedSessionRow'];
export type CallLogRow = components['schemas']['CallLogRow'];
export type RecordingLink = components['schemas']['RecordingLink'];
export type PoolListResponse = components['schemas']['PoolListResponse'];

export function useBookingComms(bookingCode: string | null) {
  return useQuery({
    queryKey: keys.comms.booking(bookingCode ?? ''),
    queryFn: () =>
      authedClient.request<CommsDetail>(`/v1/admin/bookings/${bookingCode}/comms`),
    enabled: bookingCode !== null && bookingCode !== '',
  });
}

function useCommsMutation<TArgs, TData>(fn: (args: TArgs) => Promise<TData>) {
  const client = useQueryClient();
  return useMutation<TData, unknown, TArgs>({
    mutationFn: fn,
    onSuccess: () => client.invalidateQueries({ queryKey: keys.comms.all() }),
  });
}

/** Stops two people reaching each other, so the reason is required and audited. */
export function useSuspendComms() {
  return useCommsMutation(({ id, reason }: { id: number; reason: string }) =>
    authedClient.request<MaskedSessionRow>(`/v1/admin/comms/sessions/${id}/suspend`, {
      method: 'POST',
      body: { reason },
    }),
  );
}

/** Lifts a suspension. A closed window does not reopen — the server refuses that. */
export function useReopenComms() {
  return useCommsMutation((id: number) =>
    authedClient.request<MaskedSessionRow>(`/v1/admin/comms/sessions/${id}/reopen`, {
      method: 'POST',
    }),
  );
}

/**
 * One recording, on a deliberate click.
 *
 * A mutation rather than a query on purpose: a query would refetch on focus and on mount, and
 * every one of those would be another audited access to a private conversation that nobody
 * actually asked to hear.
 */
export function useRecordingLink() {
  return useMutation<RecordingLink, unknown, number>({
    mutationFn: (callLogId: number) =>
      authedClient.request<RecordingLink>(`/v1/admin/comms/recordings/${callLogId}`),
  });
}

export function useNumberPool() {
  return useQuery({
    queryKey: keys.comms.pool(),
    queryFn: () => authedClient.request<PoolListResponse>('/v1/admin/comms/pool'),
  });
}

/**
 * Retire a pooled number, or bring it back. Never a delete: call logs and sessions point at the
 * row, and a session already on a retired number keeps it until its window closes.
 */
export function useSetPoolNumberActive() {
  return useCommsMutation(({ id, isActive }: { id: number; isActive: boolean }) =>
    authedClient.request(`/v1/admin/comms/pool/${id}`, {
      method: 'PATCH',
      body: { is_active: isActive },
    }),
  );
}

export function useAddPoolNumber() {
  return useCommsMutation((body: { phone: string; city_id?: number }) =>
    authedClient.request('/v1/admin/comms/pool', { method: 'POST', body }),
  );
}
